import { logError } from 'fergy-core-react-logging';
import { cloneElement, forwardRef, useState } from 'react';
import { type FieldError, type FieldErrors } from 'react-hook-form';
import { type AnalyticsEvent, AnalyticsHelper } from '../../../helpers/analytics/analytics.helper';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { ClickableElement } from '../../buttons';
import { CharacterCount } from '../../common-components/character-count/character-count.component';
import { InputMessage } from '../../common-components/input-message/input-message.component';
import { MaybeLabel } from '../../common-components/label/label.component';
import VisibilityIcon from '../../svgs/visibility.svg';
import { INPUT_SIZE_MAP, type InputSize } from './input.types';

export type BaseTextInputProps = {
	autoCapitalize?: string;
	autoCorrect?: string;
	autoComplete?: string;
	automationHook?: string;
	analyticsHook?: string;
	className?: string; // classes for the container around the input
	defaultValue?: string;
	description?: string;
	disabled?: boolean;
	icon?: JSX.Element;
	id?: string;
	inputClassName?: string; // classes for the input element
	inputMode?: InputMode;
	invalid?: boolean;
	invalidMessage?: string | FieldError | FieldErrors;
	maxLength?: number;
	max?: string | number;
	message?: JSX.Element;
	min?: string | number;
	name?: string;
	onBlur?: (data: React.FocusEvent<HTMLInputElement>) => void;
	omitBaseClasses?: boolean;
	onChange?: (data: React.ChangeEvent<HTMLInputElement>) => void;
	onFocus?: (data: React.FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (data: React.KeyboardEvent<HTMLInputElement>) => void;
	onSubmit?: (data: React.FormEvent<HTMLElement>) => void;
	pattern?: string;
	placeholder?: string;
	required?: boolean;
	step?: number;
	testId?: string;
	type?: string;
	ariaLabel?: string;
	value?: string | number;
	// Analytics data that will sent upon clicking or focus into the input.
	analyticsEvent?: AnalyticsEvent;
	readOnly?: boolean;
	borderDark?: boolean;
	size?: InputSize;
	leading?: JSX.Element;
	trailing?: JSX.Element;
	includeCounter?: boolean;
	maskContent?: boolean;
};

export type InputMode = 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';

export type PasswordInputProps = Omit<BaseTextInputProps, 'type'> & {
	label: string;
	id: string;
};

export type InputIconProps = {
	isInvalid: boolean;
};

const BaseTextInput = forwardRef<HTMLInputElement, BaseTextInputProps>(
	(
		{
			className = '',
			description,
			icon,
			id,
			invalid,
			invalidMessage,
			omitBaseClasses,
			required = false,
			testId,
			type = 'text',
			automationHook,
			analyticsHook,
			analyticsEvent,
			onFocus,
			borderDark,
			size = 'DEFAULT',
			leading,
			trailing,
			ariaLabel,
			inputClassName = 'ph3',
			includeCounter = false,
			value,
			maxLength,
			maskContent,
			...remainingProps
		},
		ref
	) => {
		const [currentCount, setCurrentCount] = useState(value?.toString().length || 0);

		let containerClasses = `input-reset br2 ba w-100 truncate flex items-stretch ${className}`.trim();
		if (invalid && !omitBaseClasses) {
			containerClasses = `${containerClasses} b--theme-error`;
		} else if (!omitBaseClasses) {
			containerClasses = `${containerClasses} theme-grey-darker ${borderDark ? 'b--theme-grey-darker' : 'b--theme-grey-light'}`;
		} else {
			containerClasses = className;
		}

		const focusHandler = analyticsEvent
			? (e: React.FocusEvent<HTMLInputElement>) => {
					if (onFocus) {
						onFocus(e);
					}
					AnalyticsHelper.track(analyticsEvent.data).catch(logError);
			  }
			: onFocus;

		return (
			<div className="flex flex-column">
				{/* tslint:disable-next-line: react-a11y-required  We are abiding by this rule at runtime */}
				<div className={containerClasses} data-testid="input-container">
					{leading}
					<input
						id={id}
						className={`truncate bw0 w-100 color-inherit ${inputClassName} ${INPUT_SIZE_MAP[size]}`}
						type={type}
						aria-required={required}
						required={required}
						data-testid={testId}
						data-automation={generateDataSelector('input', automationHook)}
						data-analytics={generateDataSelector('input', analyticsHook)}
						onFocus={focusHandler}
						ref={ref}
						aria-label={ariaLabel}
						onInput={(e) => setCurrentCount(e.currentTarget.value.length)}
						value={value}
						maxLength={maxLength}
						data-cs-mask={maskContent}
						{...remainingProps}
					/>
					{icon}
					{trailing}
				</div>
				{(includeCounter || invalid || description) && (
					<div className="flex justify-between w-auto">
						<InputMessage description={description} invalid={invalid} invalidMessage={invalidMessage} />
						{includeCounter && (
							<CharacterCount testId={testId} invalid={invalid} currentCount={currentCount} maxLength={maxLength} />
						)}
					</div>
				)}
			</div>
		);
	}
);

export type TextInputProps = Omit<BaseTextInputProps, 'leading' | 'trailing' | 'ariaLabel'> & {
	boldLabel?: boolean;
	helpTextTooltip?: React.ReactElement | string;
	leadingConstant?: string;
	trailingConstant?: string;
	padIcon?: boolean;
	iconOnClick?: () => void;
	maskContent?: boolean;
} & (
		| {
				label: string; // if label specified, aria-label is optional
				ariaLabel?: string;
		  }
		| {
				label?: string;
				ariaLabel: string;
		  }
	);

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
	(
		{
			className = '',
			id,
			label,
			helpTextTooltip,
			required,
			icon,
			ariaLabel,
			iconOnClick,
			disabled,
			boldLabel,
			leadingConstant,
			trailingConstant,
			borderDark,
			padIcon = true,
			maskContent,
			...remainingProps
		},
		ref
	) => {
		const renderIcon: JSX.Element | undefined =
			icon && iconOnClick ? (
				<ClickableElement
					disabled={disabled}
					className={`${padIcon ? 'ph3' : ''} self-center flex items-center`}
					ariaLabel={`${ariaLabel}-icon`}
					onClick={iconOnClick}>
					{icon}
				</ClickableElement>
			) : icon ? (
				cloneElement(icon, {
					className: `${icon.props.className}${padIcon ? ' ph3' : ''} self-center`
				})
			) : undefined;

		const constantClassName = `ph2 bg-theme-grey-lighter br2 f6 theme-grey-darker flex items-center ${
			borderDark ? 'b--theme-grey-darker' : 'b--theme-grey-light'
		}`;

		const leading = leadingConstant ? <div className={`${constantClassName} br--left br`}>{leadingConstant}</div> : undefined;
		const trailing = trailingConstant ? <div className={`${constantClassName} br--right bl`}>{trailingConstant}</div> : undefined;

		return (
			<MaybeLabel
				className={className}
				label={label}
				id={id}
				required={required}
				helpTextTooltip={helpTextTooltip}
				boldLabel={boldLabel}>
				<div className="relative w-100">
					<BaseTextInput
						id={id}
						className={label ? undefined : className}
						required={required}
						icon={renderIcon}
						ref={ref}
						trailing={trailing}
						leading={leading}
						disabled={disabled}
						borderDark={borderDark}
						ariaLabel={ariaLabel}
						maskContent={maskContent}
						{...remainingProps}
					/>
				</div>
			</MaybeLabel>
		);
	}
);

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>((props, ref) => {
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const togglePassword = (): void => {
		setIsPasswordVisible(!isPasswordVisible);
	};
	const inputType = isPasswordVisible ? 'text' : 'password';
	const iconTheme = isPasswordVisible ? 'theme-primary' : 'theme-grey';

	return (
		<TextInput
			icon={<VisibilityIcon className={`${iconTheme} f4`} data-testid="VisibilityIcon" />}
			iconOnClick={togglePassword}
			type={inputType}
			{...props}
			ref={ref}
		/>
	);
});
